import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import {
  Scale,
  Banknote,
  PlugZap,
  Baby,
  Car,
  Gavel,
  ShieldCheck,
  FileCheck2,
  CircleDollarSign,
  Send,
} from "lucide-react";

const matterRules = [
  {
    icon: Scale,
    title: "Court matters",
    identifier: "Case number (required)",
    evidence: "First page of the complaint or court matter",
    fields: "First name, last name, case number, state, county, court/district, filed date",
  },
  {
    icon: Banknote,
    title: "Loan matters",
    identifier: "Loan or account number",
    evidence: "The promissory note &/or monthly loan statement",
    fields: "First name, last name, identifier, state, county",
  },
  {
    icon: PlugZap,
    title: "Utility matters",
    identifier: "Account number",
    evidence: "Monthly utility statement",
    fields: "First name, last name, identifier, state, county",
  },
  {
    icon: Baby,
    title: "Birth certificates",
    identifier: "Certificate / state file number",
    evidence: "Copy of the birth certificate",
    fields: "First name, last name, certificate number, state, county",
  },
  {
    icon: Car,
    title: "Traffic tickets",
    identifier: "Ticket / citation number",
    evidence: "Copy of the ticket or citation",
    fields: "First name, last name, citation number, state, county",
  },
  {
    icon: Gavel,
    title: "Warrants",
    identifier: "Warrant number — as listed on the warrant (not the case number)",
    evidence: "Copy of the warrant",
    fields: "First name, last name, warrant number, state, county",
  },
];

export default function InfoPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <div className="space-y-4">
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            Information &amp; Service Disclosure
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            GetYourQSIP provides a controlled membership and report-request workflow. Members submit
            structured matter information with supporting documents, receive an automated comparison between
            their entries and the document, resolve discrepancies, pay per verified request, and our operations
            team processes the resulting verified data. This is a document information service for educational
            and research purposes only — it does not determine legal rights, ownership, liability, or case
            outcomes, and does not replace legal, financial, securities, or compliance professionals. See the
            full{" "}
            <Link to="/terms" className="text-primary hover:underline" data-testid="info-terms-link">
              Terms of Service &amp; User Agreement
            </Link>
            .
          </p>
        </div>

        <Separator />

        <section className="space-y-5">
          <h2 className="font-display text-xl md:text-2xl font-semibold">The complete journey</h2>
          <ol className="space-y-4">
            {[
              { icon: ShieldCheck, text: "Create an account and purchase a membership tier — Basic ($9.99), Pro ($29.99), or Enterprise ($59.99), monthly or annual. Active membership unlocks the Order Your QSIP workflow." },
              { icon: FileCheck2, text: "Submit one matter with one supporting document. Our extraction engine converts the file (including scanned documents via OCR), our parser extracts comparable fields, and the decision engine compares every required field. Exact matches become verified; any missing or different value requires your review and confirmation." },
              { icon: CircleDollarSign, text: "Purchase verified or customer-confirmed requests at $250 per report. Basic memberships check out up to 3 reports at a time (10 per month), Pro up to 10 (50 per month), and Enterprise up to 25 with unlimited monthly reports. Prices and quantities are always server-calculated." },
              { icon: Send, text: "Paid requests enter the administrative operations queue and progress through processing, on hold, completed, or cancelled — with dashboard and email notifications along the way. Processing by our operations team can take anywhere from 30 to 60 days." },
            ].map((step, i) => (
              <li key={i} className="flex gap-4 rounded-xl border border-border bg-card p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
                  <step.icon className="h-5 w-5 text-primary" />
                </span>
                <p className="text-sm text-muted-foreground leading-relaxed pt-1">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="space-y-5">
          <h2 className="font-display text-xl md:text-2xl font-semibold">Matter requirements</h2>
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
            {matterRules.map((rule) => (
              <Card key={rule.title} className="bg-card border-border">
                <CardHeader className="pb-3">
                  <rule.icon className="h-5 w-5 text-primary mb-1" />
                  <CardTitle className="text-base font-display">{rule.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs text-muted-foreground">
                  <p><span className="text-foreground font-medium">Identifier:</span> {rule.identifier}</p>
                  <p><span className="text-foreground font-medium">Evidence:</span> {rule.evidence}</p>
                  <p><span className="text-foreground font-medium">Verified fields:</span> {rule.fields}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6 space-y-3">
          <h2 className="font-display text-lg font-semibold">Verification &amp; processing rules</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
            <li>Missing extracted values require review — they are never treated as matches.</li>
            <li>State names and two-letter codes are treated as equal (Florida = FL).</li>
            <li>Common date formats are normalized before comparison (03/15/2024 = 2024-03-15).</li>
            <li>Only verified and customer-confirmed requests are eligible for report checkout.</li>
            <li>Paid requests cannot be repurchased; checkout limits follow your membership tier.</li>
            <li>Processing by our operations team can take anywhere from 30 to 60 days.</li>
            <li>Documents are stored privately and served only to their owner or administrators.</li>
            <li>Finished reports are attached to your order for dashboard download and delivered by email.</li>
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
